import mammoth from 'mammoth';

// PDF.js dynamic import with enhanced error handling
let pdfjsLib = null;
let pdfWorkerInitialized = false;
let workerInitializationAttempted = false;

// Initialize PDF.js with comprehensive fallback strategies
const initializePDFJS = async () => {
  if (pdfjsLib && pdfWorkerInitialized) {
    return pdfjsLib;
  }

  if (workerInitializationAttempted && !pdfWorkerInitialized) {
    throw new Error('PDF processing is not available - previous initialization failed');
  }

  workerInitializationAttempted = true;

  try {
    console.log('📄 Initializing PDF.js library...');
    
    // Try to import pdfjs-dist with timeout
    pdfjsLib = await Promise.race([
      import('pdfjs-dist'),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('PDF.js import timeout')), 15000)
      )
    ]);
    
    console.log('✅ PDF.js library imported successfully');

    // Setup worker with comprehensive fallback strategies
    if (typeof window !== 'undefined') {
      const workerSources = [
        // Try inline worker first (most reliable)
        null, // This will trigger inline worker creation
        // CDN fallbacks with specific versions
        'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js',
        'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js',
        'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js'
      ];

      let workerSuccess = false;

      // Try each worker source with enhanced testing
      for (let i = 0; i < workerSources.length; i++) {
        try {
          if (workerSources[i] === null) {
            // Try inline worker first
            console.log('📄 Attempting inline PDF worker...');
            
            // Use inline worker for better compatibility
            const workerBlob = new Blob([`
              // Inline PDF.js worker
              importScripts('https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js');
            `], { type: 'application/javascript' });
            
            const workerUrl = URL.createObjectURL(workerBlob);
            pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
          } else {
            console.log(`📄 Configuring PDF worker source ${i}: ${workerSources[i]}`);
            pdfjsLib.GlobalWorkerOptions.workerSrc = workerSources[i];
          }
          
          // Test the worker with a more comprehensive test
          await testPDFWorker(pdfjsLib);
          
          console.log(`✅ PDF worker ${i + 1} is working correctly`);
          pdfWorkerInitialized = true;
          workerSuccess = true;
          break;
        } catch (workerError) {
          console.warn(`⚠️ PDF worker source ${i + 1} failed:`, workerError.message);
          
          if (i === workerSources.length - 1) {
            console.error('❌ All PDF worker sources failed');
          }
        }
      }

      if (!workerSuccess) {
        throw new Error('All PDF worker sources failed to initialize');
      }
    } else {
      throw new Error('Window object not available - not in browser environment');
    }

    return pdfjsLib;
  } catch (error) {
    console.error('❌ Failed to initialize PDF.js:', error);
    pdfWorkerInitialized = false;
    
    // Provide specific error messages based on the failure type
    let errorMessage = 'PDF processing is temporarily unavailable.';
    
    if (error.message.includes('import') || error.message.includes('timeout')) {
      errorMessage = 'PDF library failed to load. This may be due to network issues or browser compatibility.';
    } else if (error.message.includes('worker')) {
      errorMessage = 'PDF worker failed to initialize. Browser security settings may be blocking PDF processing.';
    }
    
    throw new Error(`${errorMessage}\n\n💡 Alternative solutions:\n• Upload as Word document (.docx) - more reliable\n• Copy and paste text into a .txt file\n• Try a different browser (Chrome/Firefox recommended)`);
  }
};

// Enhanced PDF worker testing with better validation
const testPDFWorker = async (pdfjs) => {
  console.log('🧪 Testing PDF worker functionality...');
  
  try {
    // Create a minimal valid PDF for testing
    const testPDF = new Uint8Array([
      0x25, 0x50, 0x44, 0x46, 0x2d, 0x31, 0x2e, 0x34, 0x0a, // %PDF-1.4
      0x31, 0x20, 0x30, 0x20, 0x6f, 0x62, 0x6a, 0x0a, // 1 0 obj
      0x3c, 0x3c, 0x2f, 0x54, 0x79, 0x70, 0x65, 0x2f, 0x43, 0x61, 0x74, 0x61, 0x6c, 0x6f, 0x67, 0x2f, 0x50, 0x61, 0x67, 0x65, 0x73, 0x20, 0x32, 0x20, 0x30, 0x20, 0x52, 0x3e, 0x3e, 0x0a, // <</Type/Catalog/Pages 2 0 R>>
      0x65, 0x6e, 0x64, 0x6f, 0x62, 0x6a, 0x0a, // endobj
      0x78, 0x72, 0x65, 0x66, 0x0a, // xref
      0x30, 0x20, 0x32, 0x0a, // 0 2
      0x30, 0x30, 0x30, 0x30, 0x30, 0x30, 0x30, 0x30, 0x30, 0x30, 0x20, 0x36, 0x35, 0x35, 0x33, 0x35, 0x20, 0x66, 0x20, 0x0a, // 0000000000 65535 f
      0x74, 0x72, 0x61, 0x69, 0x6c, 0x65, 0x72, 0x0a, // trailer
      0x3c, 0x3c, 0x2f, 0x53, 0x69, 0x7a, 0x65, 0x20, 0x32, 0x2f, 0x52, 0x6f, 0x6f, 0x74, 0x20, 0x31, 0x20, 0x30, 0x20, 0x52, 0x3e, 0x3e, 0x0a, // <</Size 2/Root 1 0 R>>
      0x73, 0x74, 0x61, 0x72, 0x74, 0x78, 0x72, 0x65, 0x66, 0x0a, // startxref
      0x39, 0x0a, // 9
      0x25, 0x25, 0x45, 0x4f, 0x46 // %%EOF
    ]);

    const loadingTask = pdfjs.getDocument({
      data: testPDF,
      useSystemFonts: true,
      verbosity: 0, // Suppress console logs during testing
      useWorkerFetch: false,
      disableRange: true,
      disableStream: true,
      stopAtErrors: false,
      maxImageSize: 1024 * 1024
    });

    // Test with timeout and proper cleanup
    const pdf = await Promise.race([
      loadingTask.promise,
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Worker test timeout after 10 seconds')), 10000)
      )
    ]);

    if (!pdf) {
      throw new Error('Worker test failed - no PDF object returned');
    }

    // Verify basic PDF properties
    if (typeof pdf.numPages !== 'number') {
      throw new Error('Worker test failed - invalid PDF structure');
    }

    // Clean up test resources
    pdf.destroy();
    
    console.log('✅ PDF worker test completed successfully');
    return true;

  } catch (error) {
    console.error('❌ PDF worker test failed:', error);
    throw new Error(`Worker functionality test failed: ${error.message}`);
  }
};

/**
 * Document processing utilities for Knowledge Base
 */
export const SUPPORTED_FILE_TYPES = {
  'application/pdf': {
    extensions: ['.pdf'],
    processor: 'pdf',
    icon: 'FiFileText',
    color: 'text-red-600',
    description: 'PDF Document'
  },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    extensions: ['.docx'],
    processor: 'docx',
    icon: 'FiFile',
    color: 'text-blue-600',
    description: 'Word Document'
  },
  'application/msword': {
    extensions: ['.doc'],
    processor: 'doc',
    icon: 'FiFile',
    color: 'text-blue-600',
    description: 'Word Document (Legacy)'
  },
  'text/plain': {
    extensions: ['.txt'],
    processor: 'text',
    icon: 'FiFileText',
    color: 'text-gray-600',
    description: 'Text File'
  },
  'text/markdown': {
    extensions: ['.md'],
    processor: 'text',
    icon: 'FiFileText',
    color: 'text-purple-600',
    description: 'Markdown File'
  }
};

/**
 * Get file type information
 */
export const getFileTypeInfo = (file) => {
  const mimeType = file.type;
  const extension = '.' + file.name.split('.').pop().toLowerCase();
  
  // First try by MIME type
  let typeInfo = SUPPORTED_FILE_TYPES[mimeType];
  
  // Fallback to extension matching
  if (!typeInfo) {
    for (const [mime, info] of Object.entries(SUPPORTED_FILE_TYPES)) {
      if (info.extensions.includes(extension)) {
        typeInfo = info;
        break;
      }
    }
  }
  
  return typeInfo;
};

/**
 * Check if file type is supported
 */
export const isFileTypeSupported = (file) => {
  return !!getFileTypeInfo(file);
};

/**
 * Enhanced PDF processing with comprehensive error handling
 */
export const processPDF = async (file) => {
  console.log('📄 Starting enhanced PDF processing:', file.name);
  
  try {
    // Initialize PDF.js with comprehensive error handling
    const pdfjs = await initializePDFJS();
    
    if (!pdfjs || !pdfWorkerInitialized) {
      throw new Error('PDF processing library initialization failed');
    }

    console.log('📄 Reading PDF file data...');
    const arrayBuffer = await file.arrayBuffer();
    
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      throw new Error('PDF file appears to be empty or corrupted');
    }
    
    console.log('📄 PDF file loaded successfully, size:', arrayBuffer.byteLength, 'bytes');

    // Enhanced PDF loading configuration with better error handling
    const loadingTask = pdfjs.getDocument({
      data: arrayBuffer,
      useSystemFonts: true,
      disableFontFace: false,
      verbosity: 0, // Reduce console noise
      isEvalSupported: false, // Security
      useWorkerFetch: false, // Avoid CORS issues
      disableAutoFetch: false,
      disableRange: true, // Avoid range request issues
      disableStream: true, // Avoid streaming issues
      stopAtErrors: false, // Continue processing even with minor errors
      maxImageSize: 1024 * 1024, // Limit image processing
      cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/',
      cMapPacked: true,
      standardFontDataUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/',
    });

    // Handle password protection
    loadingTask.onPassword = (callback, reason) => {
      console.error('📄 PDF is password protected');
      throw new Error('🔒 This PDF is password protected.\n\n💡 Solutions:\n• Remove password protection from the PDF\n• Convert to Word document (.docx)\n• Copy text to a .txt file');
    };

    // Enhanced error handling for loading task
    loadingTask.onUnsupportedFeature = (featureId) => {
      console.warn('📄 PDF contains unsupported feature:', featureId);
    };

    // Load PDF with timeout
    console.log('📄 Loading PDF document...');
    const pdf = await Promise.race([
      loadingTask.promise,
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('PDF loading timeout after 30 seconds - file may be too large or complex')), 30000)
      )
    ]);

    if (!pdf) {
      throw new Error('Failed to load PDF - no document object returned');
    }

    console.log('✅ PDF loaded successfully:', {
      pages: pdf.numPages,
      fingerprint: pdf.fingerprints?.[0] || 'unknown'
    });

    let fullText = '';
    let processedPages = 0;
    let errorPages = 0;
    const maxPages = Math.min(pdf.numPages, 100); // Reasonable limit

    // Process pages with enhanced error handling and progress tracking
    for (let pageNum = 1; pageNum <= maxPages; pageNum++) {
      try {
        console.log(`📄 Processing page ${pageNum}/${maxPages}...`);
        
        // Load page with timeout
        const page = await Promise.race([
          pdf.getPage(pageNum),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error(`Page ${pageNum} loading timeout`)), 10000)
          )
        ]);

        if (!page) {
          throw new Error(`Failed to load page ${pageNum}`);
        }

        // Extract text with timeout
        const textContent = await Promise.race([
          page.getTextContent({
            normalizeWhitespace: true,
            disableCombineTextItems: false
          }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error(`Page ${pageNum} text extraction timeout`)), 8000)
          )
        ]);

        // Enhanced text extraction and cleaning
        const pageText = textContent.items
          .map(item => {
            if (typeof item.str === 'string' && item.str.trim()) {
              return item.str;
            }
            return '';
          })
          .filter(str => str.length > 0)
          .join(' ')
          .replace(/\s+/g, ' ')
          .trim();

        if (pageText && pageText.length > 0) {
          fullText += `\n\n--- Page ${pageNum} ---\n${pageText}`;
          processedPages++;
          console.log(`✅ Page ${pageNum} processed: ${pageText.length} characters extracted`);
        } else {
          console.warn(`⚠️ Page ${pageNum} appears to be empty or contains no extractable text`);
          errorPages++;
        }

        // Clean up page resources
        page.cleanup();

        // Progress feedback for large documents
        if (pageNum % 5 === 0) {
          console.log(`📊 Progress: ${pageNum}/${maxPages} pages processed (${processedPages} successful, ${errorPages} with issues)`);
          // Small delay to prevent overwhelming the browser
          await new Promise(resolve => setTimeout(resolve, 50));
        }

      } catch (pageError) {
        console.warn(`⚠️ Error processing PDF page ${pageNum}:`, pageError.message);
        errorPages++;
        
        // Don't let individual page errors stop the entire process
        continue;
      }
    }

    // Clean up PDF resources
    pdf.destroy();

    // Clean and validate extracted text
    fullText = fullText
      .trim()
      .replace(/\n{4,}/g, '\n\n\n') // Reduce excessive line breaks but keep some structure
      .replace(/[ \t]{2,}/g, ' ') // Reduce excessive spaces but preserve intentional spacing
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '') // Remove control characters
      .replace(/\u00A0/g, ' ') // Replace non-breaking spaces
      .replace(/\uFEFF/g, ''); // Remove BOM

    // Enhanced content validation
    if (fullText.length < 50) {
      if (errorPages === maxPages) {
        throw new Error('❌ This PDF contains only images, scanned content, or encrypted text.\n\n💡 PDF text extraction failed because:\n• The PDF may be image-based (scanned document)\n• Text may be encrypted or protected\n• The PDF may contain only graphics\n\n🔧 Solutions that work:\n• Convert to Word document (.docx) - most reliable\n• Use OCR software to extract text first\n• Copy and paste text manually into a .txt file\n\n📝 Note: Text-based PDFs work better than scanned documents.');
      } else {
        throw new Error('❌ Very little text was extracted from this PDF.\n\n💡 This may happen because:\n• The PDF contains mostly images or graphics\n• Text formatting is preventing extraction\n• The PDF has unusual encoding\n\n🔧 Try these alternatives:\n• Convert to Word document (.docx)\n• Copy and paste text into a .txt file\n• Try a different PDF if available');
      }
    }

    // Calculate quality metrics
    const successRate = processedPages / maxPages;
    const contentQuality = successRate > 0.9 ? 'excellent' : 
                          successRate > 0.7 ? 'good' : 
                          successRate > 0.5 ? 'fair' : 'poor';

    const result = {
      content: fullText,
      metadata: {
        pages: pdf.numPages,
        processedPages,
        errorPages,
        successRate: Math.round(successRate * 100),
        extractionMethod: 'Enhanced PDF.js with inline worker',
        hasImages: errorPages > 0,
        contentQuality,
        workerSource: pdfjsLib?.GlobalWorkerOptions?.workerSrc || 'inline',
        fileSize: arrayBuffer.byteLength,
        processingTime: Date.now()
      }
    };

    console.log('🎉 PDF processing completed successfully:', {
      pages: pdf.numPages,
      processedPages,
      errorPages,
      successRate: `${Math.round(successRate * 100)}%`,
      totalChars: fullText.length,
      quality: contentQuality,
      preview: fullText.substring(0, 200) + '...'
    });

    return result;

  } catch (error) {
    console.error('❌ PDF processing error:', error);
    
    // Provide comprehensive, user-friendly error messages
    let errorMessage = error.message;
    
    if (error.message.includes('initialization failed') || error.message.includes('not available')) {
      errorMessage = '🔧 PDF processing is currently unavailable.\n\n💡 This usually happens due to:\n• Browser compatibility issues\n• Network connectivity problems\n• Security restrictions\n\n🔧 Reliable alternatives:\n• Upload as Word document (.docx) - works in all browsers\n• Copy and paste text into a .txt file\n• Try using Chrome or Firefox browser\n\n📝 Word documents process more reliably than PDFs.';
    } else if (error.message.includes('timeout')) {
      errorMessage = '⏱️ PDF processing timed out.\n\n💡 This happens when:\n• The PDF file is very large (>5MB)\n• The PDF has complex formatting\n• Network is slow\n\n🔧 Solutions:\n• Try a smaller PDF file\n• Convert to Word document (.docx)\n• Break large documents into smaller sections\n• Check your internet connection';
    } else if (error.message.includes('CORS') || error.message.includes('worker') || error.message.includes('blocked')) {
      errorMessage = '🌐 Browser security is blocking PDF processing.\n\n💡 This is a common browser security feature.\n\n🔧 Solutions that work:\n• Upload as Word document (.docx) - no restrictions\n• Copy text to a .txt file\n• Try a different browser (Chrome/Firefox)\n• Disable browser extensions temporarily\n\n📝 Word documents avoid these security restrictions.';
    } else if (error.message.includes('password') || error.message.includes('🔒')) {
      errorMessage = error.message; // Keep the specific password protection message
    } else if (error.message.includes('empty') || error.message.includes('corrupted')) {
      errorMessage = '📄 PDF file appears to be empty or corrupted.\n\n💡 Please check:\n• File downloaded completely\n• File is not corrupted\n• File is a valid PDF\n\n🔧 Try:\n• Re-downloading the PDF\n• Converting to Word document (.docx)\n• Using a different PDF file';
    } else if (error.message.includes('❌') || error.message.includes('image') || error.message.includes('scanned')) {
      errorMessage = error.message; // Keep detailed image/scanned PDF messages
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Process Word documents (.docx)
 */
export const processWordDocument = async (file) => {
  try {
    console.log('📄 Processing Word document:', file.name);
    
    const arrayBuffer = await file.arrayBuffer();
    
    if (!arrayBuffer || arrayBuffer.byteLength === 0) {
      throw new Error('Word document appears to be empty');
    }
    
    console.log('📄 Word document loaded, size:', arrayBuffer.byteLength, 'bytes');
    
    const result = await mammoth.extractRawText({ arrayBuffer });
    
    let content = result.value;
    
    if (!content || content.length < 10) {
      throw new Error('Word document appears to be empty or unreadable. It may be corrupted or password-protected.');
    }
    
    // Clean up the extracted text
    content = content
      .trim()
      .replace(/\r\n/g, '\n') // Normalize line endings
      .replace(/\n{3,}/g, '\n\n') // Reduce excessive line breaks
      .replace(/\t/g, '    ') // Convert tabs to spaces
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '') // Remove control characters
      .replace(/\u00A0/g, ' ') // Replace non-breaking spaces
      .replace(/\uFEFF/g, ''); // Remove BOM
    
    console.log('✅ Word document processing complete:', {
      totalChars: content.length,
      warnings: result.messages.length,
      preview: content.substring(0, 200) + '...'
    });
    
    if (result.messages.length > 0) {
      console.log('⚠️ Word processing warnings:', result.messages.map(m => m.message));
    }
    
    return {
      content,
      metadata: {
        extractionMethod: 'Mammoth.js text extraction',
        warnings: result.messages.length,
        warningMessages: result.messages.map(m => m.message),
        fileSize: arrayBuffer.byteLength
      }
    };
    
  } catch (error) {
    console.error('❌ Word document processing error:', error);
    
    let errorMessage = error.message;
    if (error.message.includes('zip')) {
      errorMessage = 'Word document appears to be corrupted or is not a valid .docx file. Please try re-saving the document or using a different file.';
    }
    
    throw new Error(`Word document processing failed: ${errorMessage}`);
  }
};

/**
 * Process plain text files
 */
export const processTextFile = async (file) => {
  try {
    console.log('📄 Processing text file:', file.name);
    
    let content = await file.text();
    
    if (!content || content.length < 1) {
      throw new Error('Text file appears to be empty');
    }
    
    // Clean up the text
    content = content
      .trim()
      .replace(/\r\n/g, '\n') // Normalize line endings
      .replace(/\n{3,}/g, '\n\n') // Reduce excessive line breaks
      .replace(/\t/g, '    ') // Convert tabs to spaces
      .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '') // Remove control characters
      .replace(/\u00A0/g, ' ') // Replace non-breaking spaces
      .replace(/\uFEFF/g, ''); // Remove BOM
    
    console.log('✅ Text file processing complete:', {
      totalChars: content.length,
      preview: content.substring(0, 200) + '...'
    });
    
    return {
      content,
      metadata: {
        extractionMethod: 'Direct text reading',
        encoding: 'UTF-8',
        fileSize: file.size
      }
    };
    
  } catch (error) {
    console.error('❌ Text file processing error:', error);
    throw new Error(`Text file processing failed: ${error.message}`);
  }
};

/**
 * Main document processor - routes to appropriate handler
 */
export const processDocument = async (file) => {
  console.log('🔄 Starting document processing for:', {
    name: file.name,
    type: file.type,
    size: file.size,
    lastModified: new Date(file.lastModified).toISOString()
  });

  const typeInfo = getFileTypeInfo(file);
  
  if (!typeInfo) {
    throw new Error(`❌ Unsupported file type: ${file.type || 'unknown'}\n\n💡 Supported formats:\n• PDF documents (.pdf)\n• Word documents (.docx)\n• Text files (.txt)\n• Markdown files (.md)\n\nPlease convert your file to one of these formats.`);
  }
  
  // File size validation
  const maxSize = typeInfo.processor === 'pdf' ? 10 * 1024 * 1024 : // 10MB for PDFs
                  typeInfo.processor === 'docx' ? 10 * 1024 * 1024 : // 10MB for Word docs
                  5 * 1024 * 1024; // 5MB for text files
  
  if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    throw new Error(`❌ File too large: ${Math.round(file.size / (1024 * 1024))}MB\n\nMaximum size for ${typeInfo.description}: ${maxSizeMB}MB\n\n💡 Try:\n• Splitting large documents into smaller parts\n• Compressing the file\n• Converting to a different format`);
  }
  
  console.log('🔄 Processing document:', {
    name: file.name,
    type: file.type,
    size: `${Math.round(file.size / 1024)}KB`,
    processor: typeInfo.processor,
    description: typeInfo.description
  });
  
  let result;
  
  try {
    switch (typeInfo.processor) {
      case 'pdf':
        result = await processPDF(file);
        break;
      case 'docx':
      case 'doc':
        result = await processWordDocument(file);
        break;
      case 'text':
        result = await processTextFile(file);
        break;
      default:
        throw new Error(`No processor available for file type: ${typeInfo.processor}`);
    }
  } catch (processingError) {
    console.error('❌ Document processing failed:', processingError);
    throw processingError;
  }
  
  // Calculate word count with better accuracy
  const wordCount = result.content
    .split(/\s+/)
    .filter(word => word.length > 0 && /[a-zA-Z0-9]/.test(word))
    .length;
  
  // Validate content quality
  if (result.content.length < 10) {
    throw new Error('❌ Document content is too short or appears to be empty.\n\n💡 Please check:\n• File contains readable text\n• File is not corrupted\n• File format is supported');
  }
  
  if (result.content.length > 2000000) { // 2MB text limit
    throw new Error('❌ Document content is too long for optimal AI processing.\n\n💡 Please:\n• Keep documents under 2,000,000 characters\n• Split large documents into smaller sections\n• Focus on the most relevant content');
  }
  
  if (wordCount < 5) {
    throw new Error('❌ Document appears to contain very little readable text.\n\n💡 This may indicate:\n• The file is mostly images or graphics\n• Text extraction failed\n• File format issues\n\nTry converting to a different format or check the file content.');
  }
  
  console.log('🎉 Document processing completed successfully:', {
    contentLength: result.content.length,
    wordCount,
    processor: typeInfo.processor,
    quality: result.metadata.contentQuality || 'processed',
    extractionMethod: result.metadata.extractionMethod
  });
  
  return {
    content: result.content,
    wordCount,
    size: file.size,
    type: file.type,
    fileTypeInfo: typeInfo,
    metadata: {
      ...result.metadata,
      originalFileName: file.name,
      processingDate: new Date().toISOString()
    }
  };
};

/**
 * Get supported file extensions as a string for input accept attribute
 */
export const getSupportedExtensions = () => {
  const extensions = Object.values(SUPPORTED_FILE_TYPES)
    .flatMap(info => info.extensions);
  return extensions.join(',');
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};